import React from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shield, Flag, Trash2, Ban, Users, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReportsQueue from "@/components/admin/ReportsQueue";
import ModerationLog from "@/components/admin/ModerationLog";
import UsersPanel from "@/components/admin/UsersPanel";
import FiltersPanel from "@/components/admin/FiltersPanel";

const STATS = [
  { key: "open_reports", label: "Open reports", icon: Flag, accent: "#E024A3" },
  { key: "removed_total", label: "Removed", icon: Trash2, accent: "#C084FC" },
  { key: "auto_blocked_week", label: "Auto-blocked (7d)", icon: Zap, accent: "#FFB800" },
  { key: "banned_users", label: "Banned", icon: Ban, accent: "#F87171" },
  { key: "total_users", label: "Members", icon: Users, accent: "#00A3B4" },
];

export default function Admin() {
  const { user } = useAuth();
  const { data: stats = {} } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    enabled: !!user?.is_admin,
    refetchInterval: 30000,
  });
  if (!user?.is_admin) return <Navigate to="/" replace />;

  return (
    <div data-testid="admin-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#6C0BA9] flex items-center justify-center shadow-lg shadow-[#6C0BA9]/40"><Shield className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">Moderation</h1>
          <p className="text-xs text-muted-foreground">Review reports, remove content and keep Xealth healthy.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STATS.map((s) => (
          <div key={s.key} data-testid={`admin-stat-${s.key}`} className="bg-card border border-border rounded-2xl p-4">
            <s.icon className="w-4 h-4 mb-2" style={{ color: s.accent }} />
            <div className="font-heading text-2xl font-extrabold">{stats[s.key] ?? "–"}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="bg-secondary rounded-xl w-full sm:w-auto overflow-x-auto justify-start">
          <TabsTrigger data-testid="admin-tab-reports" value="reports" className="rounded-lg">Reports</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-log" value="log" className="rounded-lg">Removal log</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-lg">Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-filters" value="filters" className="rounded-lg">Auto-filters</TabsTrigger>
        </TabsList>
        <TabsContent value="reports" className="mt-4"><ReportsQueue /></TabsContent>
        <TabsContent value="log" className="mt-4"><ModerationLog /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersPanel /></TabsContent>
        <TabsContent value="filters" className="mt-4"><FiltersPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
